export type ClerkThemeResolvedState = {
  isDark: boolean;
  primary: string;
  radius: number;
  /**
   * Optional surface colors read from the live theme (foundation.css CSS variables) so the in-app Clerk
   * components mirror the app's single source of truth. When omitted (e.g. SSR, no DOM), the constants below
   * are used as a fallback — they are kept in sync with foundation.css.
   */
  background?: string;
  inputBackground?: string;
};

export type ClerkThemeVariables = {
  colorPrimary: string;
  colorDanger: string;
  colorSuccess: string;
  colorWarning: string;
  colorText: string;
  colorTextSecondary: string;
  colorBackground: string;
  colorInputBackground: string;
  colorInputText: string;
  colorNeutral: string;
  borderRadius: string;
  spacing: string;
  fontFamily: string;
};

const CLERK_THEME_LIGHT = {
  colorText: "#000000",
  colorTextSecondary: "#747686",
  colorBackground: "#FFFFFF",
  colorInputBackground: "#FFFFFF",
  colorInputText: "#000000",
  colorNeutral: "#000000",
} as const;

// Kept in sync with foundation.css dark tokens (--popover, --input, --ease-ink-muted) so the fallback
// matches even without a live DOM read.
const CLERK_THEME_DARK = {
  colorText: "#FFFFFF",
  colorTextSecondary: "#A1A1A1",
  colorBackground: "#1C1C1C",
  colorInputBackground: "#1A1A1A",
  colorInputText: "#FFFFFF",
  colorNeutral: "#FFFFFF",
} as const;

export const CLERK_THEME_BASE = {
  colorDanger: "#EF4444",
  colorSuccess: "#22C543",
  colorWarning: "#F36B16",
  spacing: "0.875rem",
  fontFamily: 'Nunito, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
} as const;

export function buildClerkThemeVariables(
  resolved: ClerkThemeResolvedState,
): ClerkThemeVariables {
  const palette = resolved.isDark ? CLERK_THEME_DARK : CLERK_THEME_LIGHT;

  return {
    colorPrimary: resolved.primary,
    colorDanger: CLERK_THEME_BASE.colorDanger,
    colorSuccess: CLERK_THEME_BASE.colorSuccess,
    colorWarning: CLERK_THEME_BASE.colorWarning,
    colorText: palette.colorText,
    colorTextSecondary: palette.colorTextSecondary,
    // Prefer the live theme values (foundation.css) — the constants are only a fallback.
    colorBackground: resolved.background ?? palette.colorBackground,
    colorInputBackground: resolved.inputBackground ?? palette.colorInputBackground,
    colorInputText: palette.colorInputText,
    colorNeutral: palette.colorNeutral,
    borderRadius: `${resolved.radius}rem`,
    spacing: CLERK_THEME_BASE.spacing,
    fontFamily: CLERK_THEME_BASE.fontFamily,
  };
}
